import SosButton from '@/customComponents/trackDriver/SosButton.jsx'
/**
 * THE BOX THE MAP LIVES IN — a card that opens to full screen, and the honest empty state
 * that stands in for the map before there is anything to draw.
 *
 * ── ⚠️ WHY THERE IS AN EMPTY STATE AT ALL ─────────────────────────────────────────
 * The driver's position exists only as a broadcast. Nothing replays the last one — not the
 * trip resource, not any endpoint — so from the moment this page opens until the driver's
 * next ping there is genuinely no car to put on a map. That gap is 3 to 10 seconds
 * normally and unbounded if the driver's phone has locked, because there is no background
 * GPS on the web.
 *
 * An empty map during that window is the worst of both: it looks like a working map that
 * has lost the car. Saying "waiting for your driver's location" is the truth, and it is
 * also the difference between a passenger waiting calmly and one calling the office.
 *
 * ── ⚠️ AND WHY STALENESS IS SHOWN RATHER THAN HIDDEN ──────────────────────────────
 * Once a position HAS arrived the car stays where it was put. A driver whose tab went to
 * the background stops sending and the pin sits still, looking live, while the car drives
 * away from it. A passenger deciding whether to walk outside is entitled to know which of
 * those they are looking at.
 */
/**
 * "Last updated 4 min ago" — rather than "Last updated 247s ago".
 *
 * ⚠️ SECONDS ALONE STOPPED BEING READABLE the moment the last fix started surviving a
 * reload. A driver whose phone locked overnight comes back as five figures of seconds,
 * which is a number a passenger has to do arithmetic on to learn the one thing it is
 * there to tell them.
 */
function agoText(seconds) {
    if (!Number.isFinite(seconds)) return ''
    if (seconds < 60) return `${seconds}s ago`
    const minutes = Math.round(seconds / 60)
    if (minutes < 60) return `${minutes} min ago`
    const hours = Math.round(minutes / 60)
    return hours === 1 ? '1 hour ago' : `${hours} hours ago`
}

export default function MapStage({children, driver, onExpand, onCollapse, full = false}) {
    const {hasPosition, isStale, secondsSince} = driver || {}

    const frame = full
        ? 'absolute inset-0'
        : 'relative h-[320px] w-full overflow-hidden rounded-(--radius-xl) border border-(--border-color)'

    return (
        <div className={frame}>
            {/* ⚠️ THE MAP IS ALWAYS DRAWN. It used to be replaced by a grey panel until a
                position arrived, which put an apology where the map goes on the one screen
                someone opens to look at a map. `TrackDriverMap` shows the booked ride in
                the meantime — a real journey, missing only the car. */}
            {children}

            {/* ONE PILL, TWO THINGS TO SAY, and never both at once.
                No position yet, or one that has stopped moving. Both are the same shape of
                fact — how current is what I am looking at — so they share the same quiet
                strip along the bottom rather than one of them taking over the view. */}
            {(!hasPosition || isStale) && (
                <div className="pointer-events-none absolute inset-x-4 bottom-4 z-10 flex justify-center">
                    <div className="flex items-center gap-2 rounded-full bg-(--text-primary)/90 px-4 py-2 text-center text-sm text-white">
                        {hasPosition
                            ? `Last updated ${agoText(secondsSince)}`
                            : <><span className="h-1.5 w-1.5 animate-pulse rounded-full bg-(--accent-green)"/>Waiting for your driver&apos;s location</>}
                    </div>
                </div>
            )}

            {/* ⚠️ THE WHOLE CARD IS THE CONTROL, not a corner button. The map underneath
                takes pointer events for panning, so a passenger dragging it would fight the
                tap target; making the overlay the button means one clear gesture. It sits
                ON TOP of the map and is transparent, which is deliberate — the map is a
                preview here, not something to be driven. */}
            {onExpand && (
                <button
                    type="button"
                    onClick={onExpand}
                    aria-label="Open the map full screen"
                    className="absolute inset-0 z-20 cursor-pointer border-0 bg-transparent p-0"
                />
            )}

            {/* ⚠️ BLACK, NOT WHITE. The house style for a floating map control is a white
                disc, and it is the wrong choice here: this basemap is a pale grey sheet, so
                a white circle on it is a shadow with a cross in it. The instruction banner
                beside it is already this colour, so the two read as one set of controls
                rather than two unrelated marks. */}
            {/* SOS, to the left of the close control — same size, same row. */}
            {onCollapse && <SosButton className="absolute right-[68px] top-4 z-20"/>}

            {onCollapse && (
                <button
                    type="button"
                    onClick={onCollapse}
                    aria-label="Close the full screen map"
                    className="absolute right-4 top-4 z-20 flex h-11 w-11 cursor-pointer items-center justify-center rounded-full border-0 bg-(--text-primary) p-0 text-white shadow-[0_2px_10px_rgba(0,0,0,0.25)]"
                >
                    <svg className="h-4 w-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
                        <path d="M6 6l12 12M18 6 6 18"/>
                    </svg>
                </button>
            )}


        </div>
    )
}

export {MapStage}
